import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { Form } from 'informed';
import { useMutation, useLazyQuery } from '@apollo/client';
import { useAuth } from '@calefy-inc/authentication';
import { isNotNullish, safelyGetValues } from '../../../../../util';
import { ALL_ORGANIZATIONS } from '../../../../../queries/queries/allOrganizations';
import { graphql } from '../../../../../gql';
import { styled } from '@mui/material';
import {
  withFormStyles,
  WithFormStyles,
} from '../../../../../util/withFormStyles';
import { DisplayInlineError } from '../../../../common/ErrorHandling';
import { AdvisorProfileFormFields } from './AdvisorProfileFormFields';
import { SubmitButton } from './PolicyStreamAdvisorsMP';
import { useIsAdmin } from '../../../../../hooks';

const CREATE_ADVISOR_REFETCH_QUERIES = [
  'policystreamAdvisorsMP',
  'policystreamAdvisorsAdmin',
] as const;

const CREATE_ADVISOR = graphql(`
  mutation createAdvisorMP(
    $token: String!
    $name: String!
    $organization: ID!
    $jurisdiction: String
    $securitiesDealerName: String
    $mortgageDealerName: String
    $designationType: String
  ) {
    createNewAdvisor(
      token: $token
      name: $name
      organization: $organization
      jurisdiction: $jurisdiction
      securitiesDealerName: $securitiesDealerName
      mortgageDealerName: $mortgageDealerName
      designationType: $designationType
    ) {
      policystreamAdvisor {
        id
        name
        jurisdiction
        securitiesDealerName
        mortgageDealerName
        designationType
      }
    }
  }
`);

export const CreateNewAdvisorButton = () => {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const isAdmin = useIsAdmin();
  if (!isAdmin) {
    return null;
  }
  return (
    <>
      <SubmitButton
        sx={{ marginBottom: (theme) => theme.spacing(1) }}
        size='small'
        variant='contained'
        onClick={() => setDialogOpen(true)}
      >
        Add new advisor
      </SubmitButton>
      <NewAdvisorDialog open={dialogOpen} setOpen={setDialogOpen} />
    </>
  );
};

interface NewAdvisorDialogProps extends WithFormStyles {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}
const NewAdvisorDialog = withFormStyles()(
  ({ open, setOpen, classes }: NewAdvisorDialogProps) => {
    const { token } = useAuth();

    const [organizationOptions, setOrganizationOptions] = useState<
      Array<{ value: string; label: string }>
    >([]);
    const [getOrganizations, { data: orgData }] =
      useLazyQuery(ALL_ORGANIZATIONS);

    useEffect(() => {
      if (token) {
        void getOrganizations({ variables: { token } });
      }
    }, [token, getOrganizations]);

    useEffect(() => {
      if (orgData) {
        setOrganizationOptions(
          (orgData.allOrganizations || []).filter(isNotNullish).map((org) => ({
            value: org.id,
            label: org.name,
          })),
        );
      }
    }, [orgData]);

    const [createAdvisor, { loading: createLoading, error: createError }] =
      useMutation(CREATE_ADVISOR, {
        refetchQueries: [...CREATE_ADVISOR_REFETCH_QUERIES],
        onCompleted: () => {
          setOpen(false);
        },
      });

    return (
      <Dialog
        open={open}
        onBackdropClick={() => {
          setOpen(false);
        }}
      >
        <DialogTitle>New Advisor</DialogTitle>
        <Form
          className={classes.dialogForm}
          style={{ width: '600px' }}
          onSubmit={(formState) => {
            const {
              name: nameRaw,
              organization: organizationRaw,
              jurisdiction,
              securitiesDealerName,
              mortgageDealerName,
              designationType,
            } = safelyGetValues(
              formState.values,
              [
                'name',
                'organization',
                'jurisdiction',
                'securitiesDealerName',
                'mortgageDealerName',
                'designationType',
              ],
              (v) => v?.trim() || undefined,
            );

            const name = nameRaw || '';
            const organization = organizationRaw || '';
            if (!organization) {
              return;
            }

            createAdvisor({
              variables: {
                token,
                name,
                organization,
                jurisdiction,
                securitiesDealerName,
                mortgageDealerName,
                designationType,
              },
            });
          }}
        >
          <DialogContent>
            <AdvisorProfileFormFields
              layout='dialog'
              textfieldClassName={classes.textfield}
              nameFieldDescription="The advisor's full name as it will appear to clients."
              organizationSelectOptions={organizationOptions}
              organizationSelectClassName={classes.selectField}
            />
            <DisplayInlineError error={createError}>
              Error when creating a new advisor:
            </DisplayInlineError>
          </DialogContent>
          <StyledDialogActions>
            <Button variant='outlined' onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <LoadingButton
              loading={createLoading}
              variant='contained'
              type='submit'
              disabled={organizationOptions.length === 0}
            >
              Create New Advisor
            </LoadingButton>
          </StyledDialogActions>
        </Form>
      </Dialog>
    );
  },
);

const StyledDialogActions = styled(DialogActions)(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(4),
  };
});
